import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { IPricingPlan } from '@query'
import { Badge, Button } from '@components'
import { FaCheck } from 'react-icons/fa'
import { formatEurosWithOpts, showFeaturebaseNewMessage } from '@utils'
import { CompactPricingSuccessBadge } from './CompactPricingSuccessBadge'

interface Props {
  plan: IPricingPlan
  interval: 'month' | 'year'
  trialAvailable: boolean
  isSelected: boolean
  isCheckingOut: boolean
  isPopular: boolean
  onSelect: () => void
}

function getPriceNumber(plan: IPricingPlan, interval: 'month' | 'year'): number | null {
  if (interval === 'month' && plan.monthly_price_eur != null) {
    return Math.round(plan.monthly_price_eur / 100)
  }
  if (interval === 'year' && plan.yearly_price_eur != null) {
    return Math.round(plan.yearly_price_eur / 12 / 100)
  }
  return null
}

function getYearlyDiscount(plan: IPricingPlan): number | null {
  if (plan.monthly_price_eur == null || plan.yearly_price_eur == null) return null
  const monthlyEquivalent = plan.yearly_price_eur / 12
  const discount = Math.round((1 - monthlyEquivalent / plan.monthly_price_eur) * 100)
  return discount > 0 ? discount : null
}

export const PricingCard: React.FC<Props> = ({
  plan,
  interval,
  trialAvailable,
  isSelected,
  isCheckingOut,
  isPopular,
  onSelect
}) => {
  const [t] = useTranslation()
  const priceNumber = getPriceNumber(plan, interval)
  const discount = interval === 'year' ? getYearlyDiscount(plan) : null

  const documentFeature =
    plan.monthly_documents != null
      ? t('components.customPricingTable.documentsPerMonth', { count: plan.monthly_documents })
      : t('components.customPricingTable.unlimitedDocuments')

  const badge =
    trialAvailable && !plan.contact_sales ? (
      <Badge type="info">{t('components.customPricingTable.trialBadge')}</Badge>
    ) : isPopular ? (
      <Badge type="warning">{t('components.customPricingTable.mostPopular')}</Badge>
    ) : null

  if (plan.contact_sales) {
    const [firstPara, ...restParas] = (plan.description ?? '').split('\n\n')
    const growthFeatures = restParas.filter(Boolean)

    const handleContactSalesClick = () => {
      showFeaturebaseNewMessage(
        t('components.customPricingTable.customerlyGrowthMessage', {
          planName: plan.name
        })
      )
    }

    return (
      <StyledCard
        $selected={false}
        $contactSales
        whileHover={{ y: -4 }}
        transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      >
        {badge && <FloatingBadge>{badge}</FloatingBadge>}
        <TopSection>
          <PlanName>{plan.name}</PlanName>
          {firstPara && <PlanDescription>{firstPara}</PlanDescription>}

          <Spacer />

          {priceNumber != null && (
            <PriceRow>
              <PriceNumber>{priceNumber}</PriceNumber>
              <PriceSuffix>€</PriceSuffix>
              <PriceUnit>{t('components.customPricingTable.perMonth')}</PriceUnit>
            </PriceRow>
          )}
        </TopSection>
        <FullWidthButton type="button" onClick={handleContactSalesClick}>
          {t('components.customPricingTable.contactSales')}
        </FullWidthButton>
        {growthFeatures.length > 0 && (
          <>
            {/* <FeatureDivider /> */}
            <FeatureList>
              {growthFeatures.map((feature, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <FeatureDivider />}
                  <FeatureItem>
                    <FaCheck />
                    {feature}
                  </FeatureItem>
                </React.Fragment>
              ))}
            </FeatureList>
          </>
        )}
      </StyledCard>
    )
  }

  return (
    <StyledCard
      $selected={isSelected}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
    >
      {badge && <FloatingBadge>{badge}</FloatingBadge>}

      <TopSection>
        <PlanNameRow>
          <PlanName>{plan.name}</PlanName>
          {discount != null && (
            <CompactPricingSuccessBadge>−{discount}%</CompactPricingSuccessBadge>
          )}
        </PlanNameRow>

        {plan.description && <PlanDescription>{plan.description}</PlanDescription>}

        <Spacer />

        <PriceRow>
          {priceNumber != null ? (
            <>
              <PriceNumber>{priceNumber}</PriceNumber>
              <PriceSuffix>€</PriceSuffix>
              <PriceUnit>{t('components.customPricingTable.perMonth')}</PriceUnit>
            </>
          ) : (
            <PriceNumber>—</PriceNumber>
          )}
        </PriceRow>

        {interval === 'year' && plan.yearly_price_eur != null && (
          <YearlyNote>
            {t('components.customPricingTable.billedYearly', {
              amount: formatEurosWithOpts(plan.yearly_price_eur, { avoidDecimals: true })
            })}
          </YearlyNote>
        )}
      </TopSection>

      <FullWidthButton
        type="button"
        intent={isPopular ? 'primary' : undefined}
        showSpinner={isCheckingOut}
        onClick={event => {
          event.stopPropagation()
          onSelect()
        }}
      >
        {trialAvailable
          ? t('components.customPricingTable.startTrial')
          : t('components.customPricingTable.subscribe')}
      </FullWidthButton>

      {/* <FeatureDivider /> */}

      <FeatureList>
        <FeatureItem>
          <FaCheck />
          {documentFeature}
        </FeatureItem>
        {plan.unlimited_seats && (
          <>
            <FeatureDivider />
            <FeatureItem>
              <FaCheck />
              {t('components.customPricingTable.unlimitedSeats')}
            </FeatureItem>
          </>
        )}
      </FeatureList>
    </StyledCard>
  )
}

const StyledCard = styled(motion.div)<{
  $selected: boolean
  $contactSales?: boolean
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.xl}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 2px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.neutralGray)};
  background: ${({ theme }) => theme.colors.neutralWhite};
  flex: 1;
  min-width: 0;
  min-height: 340px;
  max-width: 420px;
  position: relative;

  &:hover {
    border-color: ${({ theme, $contactSales }) =>
      $contactSales ? theme.colors.neutralGray : theme.colors.primary};
  }
`

/* Absolutely positioned so it never shifts the plan name down */
const FloatingBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md}rem;
  right: ${({ theme }) => theme.spacing.md}rem;
`

const TopSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  height: 175px;
`

const PlanNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;
`

const PlanName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const PlanDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.5;
`

const Spacer = styled.div`
  flex: 1;
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const PriceNumber = styled.span`
  font-size: 2.6rem;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const PriceSuffix = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  align-self: flex-start;
  margin-top: 0.3rem;
`

const PriceUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-left: ${({ theme }) => theme.spacing.xxs}rem;
`

const YearlyNote = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const FullWidthButton = styled(Button)`
  width: 100%;
  justify-content: center;
  border-radius: 999px;
`

const FeatureDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  margin: 0;
`

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 80px;
`

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  padding: ${({ theme }) => theme.spacing.sm}rem 0;

  svg {
    fill: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
`
